import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { customersAPI } from "../../services";
import CustomerForm from "./CustomerForm";
import { Download, Check, ChevronDown } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import "../../style/customers.css";
import CreditCustomersIcon from "../../assets/credit-customers.svg";
import ActiveCustomersIcon from "../../assets/active-customer.svg";
import TotalCustomersIcon from "../../assets/total-customers.svg";
import TotalInventoryIcon from "../../assets/total-inventory.svg";
import AddCustomer from "../../assets/icons/AddCustomer";
import TrendUp from "../../assets/icons/TrendUp";
import TrendDown from "../../assets/icons/TrendDown";
import FilterIcon from "../../assets/icons/FilterIcon";
import EditIcon from "../../assets/icons/EditIcon";
import EyeIcon from "../../assets/icons/EyeIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";

import FilePdf from "../../assets/file-pdf.svg";
import FileExcel from "../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../assets/file-csv.svg";

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerStats, setCustomerStats] = useState({
    total_customers: { count: 0, statistics: {} },
    active_customers: { count: 0, statistics: {} },
    credit_customers: { count: 0, statistics: {} },
    outstanding_balance: { count: 0, statistics: {} },
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const filterDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // Load customers and stats from API
  const fetchCustomers = async (search = "", page = 1, type = "") => {
    setLoading(true);
    try {
      const result = await customersAPI.getCustomers(
        search,
        page,
        9999,
        type
      );
      if (result.error) {
        toast.error(result.error.message || "Failed to fetch customers");
        return;
      }

      const customersData = result.response?.data || result.response || [];
      setCustomers(customersData);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerStats = async () => {
    setStatsLoading(true);
    try {
      const result = await customersAPI.getCustomerCount();
      if (result.error) {
        console.error("Failed to fetch customer stats:", result.error.message);
        toast.error(
          result.error.message || "Failed to fetch customer statistics"
        );
        return;
      }

      // The API service now returns normalized data directly
      const stats = result.response || {};
      setCustomerStats(stats);
    } catch (error) {
      console.error("Error fetching customer stats:", error);
      toast.error("Failed to fetch customer statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const [isInitialMount, setIsInitialMount] = useState(true);
  const isMountedRef = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (isMountedRef.current) return;
    isMountedRef.current = true;

    fetchCustomers();
    fetchCustomerStats();
    setIsInitialMount(false);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search with API call
  useEffect(() => {
    // Skip on initial mount to avoid duplicate API call
    if (isInitialMount) return;

    const delayedSearch = setTimeout(() => {
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchCustomers(searchTerm, 1, type); // Always call with current searchTerm
      setCurrentPage(1); // Reset pagination on search
    }, 500); // Debounce search

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, balanceFilter]);

  // Set filtered customers to match API results (server-side filtering)
  useEffect(() => {
    setFilteredCustomers(customers);
  }, [customers]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleAddCustomer = async (customerData) => {
    try {
      const result = await customersAPI.createCustomer(customerData);

      if (result.error) {
        console.error("Backend error creating customer:", result.error);
        return result; // Return error to form for handling
      }

      toast.success("Customer added successfully!");
      setShowForm(false);
      fetchCustomers(); // Refresh the list
      fetchCustomerStats(); // Refresh stats

      return result;
    } catch (error) {
      console.error("Error adding customer:", error);
      return {
        error: {
          message: "Failed to add customer",
          originalError: error,
        },
      };
    }
  };

  const handleEditCustomer = async (customerData) => {
    try {
      const result = await customersAPI.updateCustomer(
        editingCustomer.id,
        customerData
      );

      if (result.error) {
        console.error("Backend error updating customer:", result.error);
        return result; // Return error to form for handling
      }

      toast.success("Customer updated successfully!");
      setShowForm(false);
      setEditingCustomer(null);
      fetchCustomers(); // Refresh the list
      fetchCustomerStats(); // Refresh stats

      return result;
    } catch (error) {
      console.error("Error updating customer:", error);
      return {
        error: {
          message: "Failed to update customer",
          originalError: error,
        },
      };
    }
  };

  const openEditForm = (customer) => {
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const openViewPage = (customer) => {
    navigate(`/customer/${customer.id}`);
  };

  // Use API statistics or calculate from local data as fallback
  const totalCustomers =
    customerStats.total_customers?.count || customers.length;
  const activeCustomers =
    customerStats.active_customers?.count ||
    customers.filter((c) => (c.balance || 0) >= 0).length;
  const creditCustomers =
    customerStats.credit_customers?.count ||
    customers.filter((c) => (c.balance || 0) > 0).length;
  const outstandingBalance =
    customerStats.outstanding_balance?.count ||
    customers.reduce((sum, c) => sum + (c.balance || 0), 0);

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Helper function to get trend percentage
  const getTrendPercentage = (statistics) => {
    if (
      statistics?.change_percent === null ||
      statistics?.change_percent === undefined
    ) {
      return "--";
    }
    return `${parseFloat(statistics.change_percent).toFixed(1)}%`;
  };

  // Helper function to get trend direction
  const getTrendDirection = (statistics) => {
    const trend = statistics?.trend;
    if (trend === "increase") return "up";
    if (trend === "decrease") return "down";
    return "no_change";
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / itemsPerPage));

  // Show pagination if there are more items than can fit on one page
  // Also show if we have data but it's being filtered client-side
  const shouldShowPagination = filteredCustomers.length > 0;

  const SortableHeader = ({ field, children }) => (
    <th className="sortable-header" onClick={() => handleSort(field)}>
      {children}
      {sortField === field && (
        <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
      )}
    </th>
  );

  const formatBalance = (balance) => {
    const amount = parseFloat(balance || 0);
    return `€${amount.toFixed(2)}`;
  };

  const filterOptions = [
    { value: "all", label: "All Balance" },
    { value: "positive", label: "Credit" },
    { value: "negative", label: "Debit" },
  ];

  const handleFilterSelect = (value) => {
    setBalanceFilter(value);
    setShowFilterDropdown(false);

    // Trigger API call with new filter
    const type =
      value === "positive" ? "credit" : value === "negative" ? "debit" : "";
    fetchCustomers(searchTerm, 1, type);
  };

  const getFilterLabel = () => {
    const option = filterOptions.find((opt) => opt.value === balanceFilter);
    return option ? option.label : "All Balance";
  };

  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "excel", label: "Excel", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const handleExportSelect = (value) => {
    setShowExportDropdown(false);

    switch (value) {
      case "pdf":
        // Navigate to CustomerPrint page with customer data
        navigate("/customers/print", {
          state: { customers: filteredCustomers },
        });
        break;
      case "excel":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      default:
        toast.warning(`Export format ${value} not supported`);
    }
  };

  // Helper function to safely get field value
  const getFieldValue = (value, defaultValue = "") => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    return String(value);
  };
  const exportToExcel = () => {
    // Prepare data for Excel with ID as first column, removing Status
    const excelData = filteredCustomers.map((customer) => ({
      ID: getFieldValue(customer.id),
      Name: getFieldValue(customer.name),
      Email: getFieldValue(customer.user_email || customer.email),
      Phone: getFieldValue(customer.mobile || customer.phone),
      Address: getFieldValue(customer.address),
      Balance: getFieldValue(customer.balance, "0"),
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    // Set column widths
    ws["!cols"] = [
      { wch: 10 }, // ID
      { wch: 20 }, // Name
      { wch: 30 }, // Email
      { wch: 15 }, // Phone
      { wch: 40 }, // Address
      { wch: 12 }, // Balance
    ];

    // Apply color formatting to Balance column (column F, index 5)
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let row = 1; row <= range.e.r; row++) {
      // Skip header row (row 0), start from row 1
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 5 }); // Column F (Balance)
      if (ws[cellAddress]) {
        const customer = filteredCustomers[row - 1];
        if (customer) {
          // Handle string balance like "-90.00" or numeric balance
          const balanceValue = customer.balance;
          const balance = typeof balanceValue === 'string' 
            ? parseFloat(balanceValue.replace(/[€,\s]/g, '')) || 0
            : parseFloat(balanceValue || 0);
          const color = balance >= 0 ? "#0a8900" : "#dc2626";
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          if (!ws[cellAddress].s.font) ws[cellAddress].s.font = {};
          ws[cellAddress].s.font.color = { rgb: color.substring(1) }; // Remove # for Excel
        }
      }
    }

    // Save the Excel file
    XLSX.writeFile(wb, "customers-report.xlsx");
  };

  const exportToCSV = () => {
    // Prepare CSV data with ID as first column, removing Status
    const csvHeaders = [
      "ID",
      "Name",
      "Email",
      "Phone",
      "Address",
      "Balance",
    ];
    const csvData = filteredCustomers.map((customer) => [
      getFieldValue(customer.id),
      getFieldValue(customer.name),
      getFieldValue(customer.user_email || customer.email),
      getFieldValue(customer.mobile || customer.phone),
      getFieldValue(customer.address),
      getFieldValue(customer.balance, "0"),
    ]);

    // Combine headers and data
    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "customers-report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 customers-container">
        <div className="customers-layout">
          {/* Header */}
          <div className="customers-top">
            <div className="customers-header">
              <div>
                <h2 className="customers-title">Customers</h2>
                <p className="customers-subtitle">Manage your customers</p>
              </div>

              <div className="d-flex gap-2 align-items-center">
                {/* Add Customer Button */}
                <button
                  className="add-customer-btn"
                  onClick={() => setShowForm(true)}
                >
                  <AddCustomer /> Add Customer
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Customers
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(totalCustomers)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.total_customers?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.total_customers?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.total_customers?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalCustomersIcon} alt="Total Brands" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Active Customers
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(activeCustomers)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.active_customers?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.active_customers?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.active_customers?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={ActiveCustomersIcon} alt="Total Products" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Credit Customers
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(creditCustomers)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.credit_customers?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.credit_customers?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.credit_customers?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={CreditCustomersIcon} alt="Total Brands" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Outstanding Balance
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatCurrency(outstandingBalance)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.outstanding_balance?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.outstanding_balance?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.outstanding_balance?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalInventoryIcon} alt="Total Products" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Balance Filter + Export Filter */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="customers-search-container">
                <input
                  type="text"
                  className="customers-search-input"
                  placeholder="Search customers by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src="/icons/Search.png"
                  alt="Search"
                  className="customers-search-icon"
                />
              </div>

              {/* Balance Filter */}
              <div className="customer-filter-dropdown" ref={filterDropdownRef}>
                <button
                  className="customers-filter-button"
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <FilterIcon size={16} />
                  {getFilterLabel()}
                </button>

                <div
                  className={`customer-filter-dropdown-content ${
                    showFilterDropdown ? "show" : ""
                  }`}
                >
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`customer-filter-dropdown-item ${
                        balanceFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="customer-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Export Filter */}
              <div className="customer-filter-dropdown" ref={exportDropdownRef}>
                <button
                  className="customers-filter-button"
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                >
                  <Download size={16} />
                  Export
                  <ChevronDown
                    size={12}
                    className={`customer-export-dropdown-arrow ${
                      showExportDropdown ? "rotated" : ""
                    }`}
                  />
                </button>

                <div
                  className={`customer-export-dropdown-content ${
                    showExportDropdown ? "show" : ""
                  }`}
                >
                  {exportOptions.map((option) => (
                    <div
                      key={option.value}
                      className="customer-export-dropdown-item"
                      onClick={() => handleExportSelect(option.value)}
                    >
                      <img
                        src={option.icon}
                        alt={option.label}
                        width="16"
                        height="16"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Customers Table */}
          <div className="customers-table-wrapper">
            <div className="customer-table-responsive">
              <table className="customer-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <SortableHeader field="name">NAME</SortableHeader>
                    <SortableHeader field="email">EMAIL</SortableHeader>
                    <SortableHeader field="mobile">PHONE</SortableHeader>
                    <th>ADDRESS</th>
                    <SortableHeader field="balance">BALANCE</SortableHeader>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading customers...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentCustomers.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No customers found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentCustomers.map((customer) => (
                      <tr key={customer.id}>
                        <td>{customer.id}</td>
                        <td title={customer.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.name}</td>
                        <td title={customer.user_email || customer.email} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.user_email || customer.email}</td>
                        <td>{customer.mobile || customer.phone}</td>
                        <td title={customer.address} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.address}</td>
                        <td>
                          <span
                            className={`balance-badge ${
                              (customer.balance || 0) >= 0
                                ? "positive"
                                : "negative"
                            }`}
                          >
                            {formatBalance(customer.balance)}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn view"
                              onClick={() => openViewPage(customer)}
                              title="View customer"
                            >
                              <EyeIcon className="action-icon view" />
                            </button>
                            <button
                              className="action-btn view"
                              onClick={() => openEditForm(customer)}
                              title="Edit customer"
                            >
                              <EditIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className="customers-pagination-sticky">
              <div className="customer-pagination-container">
                <div className="customer-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="customer-pagination-buttons">
                  <button
                    className="customer-pagination-btn"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      // If using server-side pagination, uncomment the next line
                      // fetchCustomers(searchTerm, newPage);
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="customer-pagination-btn"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Customer Form Modal */}
          {showForm && (
            <CustomerForm
              customer={editingCustomer}
              onSave={editingCustomer ? handleEditCustomer : handleAddCustomer}
              onCancel={() => {
                setShowForm(false);
                setEditingCustomer(null);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Customers;
